import { useEffect, useRef } from "react";
import { getParentReferralTransitionDelay, type ParentReferralOffer } from "./parentReferralOffer";

const MAX_TIMEOUT_MS = 2_147_483_647;

export function useRefreshParentReferralOffer(
  offer: ParentReferralOffer | undefined,
  receivedAt: number,
  refresh: () => unknown,
  enabled = true,
  scopeKey?: string,
) {
  const refreshedTransition = useRef<string>();
  const receipt = useRef({ scopeKey, serverTime: offer?.serverTime, receivedAt });
  if (receipt.current.scopeKey !== scopeKey || receipt.current.serverTime !== offer?.serverTime) {
    receipt.current = { scopeKey, serverTime: offer?.serverTime, receivedAt };
  }
  const offerReceivedAt = receipt.current.receivedAt;
  const transitionKey = `${scopeKey}:${offer?.nextTransitionAt}`;
  useEffect(() => {
    if (!enabled || !offer?.nextTransitionAt || refreshedTransition.current === transitionKey) return;
    let timeout: ReturnType<typeof setTimeout>;
    function schedule() {
      if (!offer) return;
      const delay = getParentReferralTransitionDelay(offer, offerReceivedAt, Date.now());
      if (delay === undefined) return;
      timeout = setTimeout(
        () => {
          if (delay > MAX_TIMEOUT_MS) schedule();
          else {
            refreshedTransition.current = transitionKey;
            void refresh();
          }
        },
        Math.min(delay, MAX_TIMEOUT_MS),
      );
    }
    schedule();
    return () => clearTimeout(timeout);
  }, [enabled, offer, offerReceivedAt, refresh, transitionKey]);
}
